import BaseApiClient from "@/api/base-client";
import { SortOption } from "@/types/external-content";
const BASE_PATH = "/api/studies";
// EC endpoint path — single source of truth shared by the client and the URL builders below.
const externalContentPath = (studyId, activityId, contentId) => {
    const base = `${BASE_PATH}/${studyId}/activities/${activityId}/external-content`;
    return contentId ? `${base}/${contentId}` : base;
};
// Browser-navigation endpoints (302→presigned-S3 redirect / xlsx attachment) — plain utils,
// not request methods: they build a URL for an <a> href / window.open, never an axios fetch
// (the /file redirect targets a presigned S3 URL that must not carry our session cookie).
export const transcriptDownloadUrl = (studyId, activityId, contentId) => `${externalContentPath(studyId, activityId, contentId)}/transcripts/primary/download`;
export const fileUrl = (studyId, activityId, contentId, download = false) => {
    const path = `${externalContentPath(studyId, activityId, contentId)}/file`;
    return download ? `${path}?download=true` : path;
};
class ExternalContentApi extends BaseApiClient {
    contentPath(studyId, activityId, contentId) {
        return externalContentPath(studyId, activityId, contentId);
    }
    getConfig = async (studyId, activityId) => {
        return this.get(`${this.contentPath(studyId, activityId)}/config`);
    };
    list = async (studyId, activityId, sort = SortOption.RECENT) => {
        const response = await this.get(this.contentPath(studyId, activityId), {
            params: { sort }
        });
        return response.contents;
    };
    create = async (studyId, activityId, data) => {
        return this.post(this.contentPath(studyId, activityId), { data });
    };
    remove = async (studyId, activityId, contentId) => {
        return this.delete(this.contentPath(studyId, activityId, contentId));
    };
    // Retry a failed upload by linking a freshly uploaded replacement file.
    retryUpload = async (studyId, activityId, contentId, resumableId) => {
        return this.post(`${this.contentPath(studyId, activityId, contentId)}/retry`, {
            data: { resumableId }
        });
    };
    getById = async (studyId, activityId, contentId) => {
        return this.get(this.contentPath(studyId, activityId, contentId));
    };
    updateMetadata = async (studyId, activityId, contentId, data) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/metadata`, {
            data
        });
    };
    // One row per *attempted* engine; absent engines have never been transcribed.
    getEngineStates = async (studyId, activityId, contentId) => {
        return this.get(`${this.contentPath(studyId, activityId, contentId)}/transcripts`);
    };
    // Omit `engine` to resolve the primary transcript — the only path non-
    // Super-Admin viewers may request. Super-Admin viewers pass an engine ID.
    getTranscript = async (studyId, activityId, contentId, engine, exportMode) => {
        const path = engine != null
            ? `${this.contentPath(studyId, activityId, contentId)}/transcripts/${engine}`
            : `${this.contentPath(studyId, activityId, contentId)}/transcripts/primary`;
        return this.get(path, {
            params: exportMode ? { export: true } : undefined
        });
    };
    // Omit `engine` to re-run the primary (moderator-allowed; the BE resolves it); pass an engine ID for the Super-Admin per-engine route.
    transcribe = async (studyId, activityId, contentId, engine) => {
        const path = engine != null
            ? `${this.contentPath(studyId, activityId, contentId)}/transcripts/${engine}`
            : `${this.contentPath(studyId, activityId, contentId)}/transcripts/primary`;
        return this.post(path);
    };
    // Submit the full post-edit segment text as one string; the BE re-tokenises
    // on whitespace and returns the new segment view plus the post-mutation
    // speakers map.
    updateSegmentText = async (studyId, activityId, contentId, segmentId, text) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary/segments/${segmentId}/text`, { data: { text } });
    };
    // Display-name override — presentation-only.
    setSpeakerName = async (studyId, activityId, contentId, label, displayName) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary/speakers/${label}/name`, { data: { displayName } });
    };
    // Role override — triggers BE re-analysis.
    setSpeakerRole = async (studyId, activityId, contentId, label, role) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary/speakers/${label}/role`, { data: { role } });
    };
    // Reassign whole segments to an existing target speaker label in one write.
    reassignSegmentsSpeaker = async (studyId, activityId, contentId, segmentIds, speaker) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary/segments/speaker`, { data: { segmentIds, speaker } });
    };
    setPrimaryEngine = async (studyId, activityId, contentId, engine) => {
        return this.put(`${this.contentPath(studyId, activityId, contentId)}/transcripts/primary`, {
            data: { engine }
        });
    };
    fetchSummary = (studyId, activityId, contentId, exportMode) => this.get(`${this.contentPath(studyId, activityId, contentId)}/summaries`, {
        params: exportMode ? { export: true } : undefined
    });
    // Generate (or regenerate) an AI summary of the given type.
    generateSummary = (studyId, activityId, contentId, contentType = "SUMMARY") => this.post(`${this.contentPath(studyId, activityId, contentId)}/summaries/${contentType}`);
    /** @deprecated use {@link generateSummary} with an explicit content type. */
    regenerateSummary = (studyId, activityId, contentId) => this.generateSummary(studyId, activityId, contentId, "SUMMARY");
    acknowledgeLiveMinutes = async (studyId, activityId) => {
        return this.post(`${this.contentPath(studyId, activityId)}/live-minute-acknowledgment`);
    };
    // Sync activity start/end dates to encompass all content source dates.
    syncDates = async (studyId, activityId) => {
        return this.put(`${this.contentPath(studyId, activityId)}/sync-dates`);
    };
    static instance = new ExternalContentApi();
}
export default ExternalContentApi.instance;
